import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { type Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import type { CommerceLanding } from '../../../domain/commerce-registry/models/commerce-landing.model';
import type { LandingDinamicaRtdb } from '../../../domain/commerce-registry/models/landing-dinamica.model';
import { CommerceLandingPort } from '../../../domain/commerce-registry/ports/commerce-landing.port';
import { ENVIRONMENT } from '../../../../environments/environment';
import { mapLandingDinamicaToCommerceLanding } from '../mappers/map-landing-dinamica-to-commerce-landing';
import { createCommerceLandingMock } from '../mocks/commerce-landing.mock';
import { applyCommerceThemeDefaults, applyMockDevFieldOverrides } from '../mocks/merge-commerce-theme-palette';

/**
 * Loads CommerceLanding from Firebase RTDB `/landings` via **REST only** (HttpClient).
 *
 * Intentionally avoids the Firebase JS SDK: under CloudFront CSP (`script-src` without
 * `*.firebaseio.com`), the SDK's long-polling transport injects blocked `<script>` tags and can
 * hang the registry guard on a blank page. REST uses XHR/fetch (`connect-src *`) and is safe.
 *
 * 1. **Indexed REST** `?orderBy="comercio_slug"&equalTo=...`
 * 2. **Full `landings.json`** when indexed returns **400** (missing `.indexOn`), then filter
 *    client-side (prefer adding `.indexOn` on `comercio_slug` under `landings` in RTDB rules).
 */
@Injectable()
export class FirebaseCommerceLandingAdapter extends CommerceLandingPort {
  constructor(private readonly http: HttpClient) {
    super();
  }

  loadBySlug(comercioSlug: string, programaSlug?: string | null): Observable<CommerceLanding | null> {
    const slug = comercioSlug.trim() || 'demo';
    const baseUrl = this.buildBaseUrl();

    if (!baseUrl) {
      return of(ENVIRONMENT.mockEnabled ? createCommerceLandingMock(slug) : null);
    }

    const indexedUrl = this.buildIndexedUrl(baseUrl, slug);
    return this.loadViaHttpIndexedThenFull(slug, programaSlug, indexedUrl, baseUrl).pipe(
      map((landing) => this.fallbackToMock(landing, slug)),
      catchError(() => (ENVIRONMENT.mockEnabled ? of(createCommerceLandingMock(slug)) : of(null))),
    );
  }

  private buildBaseUrl(): string | null {
    const fb = ENVIRONMENT.firebase;
    const base = fb?.databaseURL?.trim();
    if (!base) return null;
    const segment = (fb?.landingsCollectionPath ?? 'landings').replace(/^\/+|\/+$/g, '');
    return `${base.replace(/\/$/, '')}/${segment}.json`;
  }

  private buildIndexedUrl(baseUrl: string, slug: string): string {
    return `${baseUrl}?orderBy=${encodeURIComponent('"comercio_slug"')}&equalTo=${encodeURIComponent(JSON.stringify(slug))}`;
  }

  /**
   * Indexed REST first; full `landings.json` when indexed returns **400** (no RTDB index),
   * then filter client-side.
   */
  private loadViaHttpIndexedThenFull(
    slug: string,
    programaSlug: string | null | undefined,
    indexedUrl: string,
    baseUrl: string,
  ): Observable<CommerceLanding | null> {
    return this.http.get<unknown>(indexedUrl).pipe(
      map((raw) => this.mapPayloadToLanding(raw, slug, programaSlug)),
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse && err.status === 400) {
          // Missing RTDB `.indexOn` on comercio_slug — fall back to full collection + client filter.
          return this.http.get<unknown>(baseUrl).pipe(
            map((raw) => this.mapPayloadToLanding(raw, slug, programaSlug)),
            catchError(() => of(null)),
          );
        }
        return of(null);
      }),
    );
  }

  private mapPayloadToLanding(
    raw: unknown,
    slug: string,
    programaSlug?: string | null,
  ): CommerceLanding | null {
    const rows = this.rowsFromRtdbPayload(raw);
    const filtered = this.filterBySlug(rows, slug);
    const row = this.pickLandingRow(filtered, programaSlug);
    if (!row) return null;
    const mapped = mapLandingDinamicaToCommerceLanding(row, slug);
    const themed = applyCommerceThemeDefaults(mapped);
    return applyMockDevFieldOverrides(themed);
  }

  private rowsFromRtdbPayload(raw: unknown): LandingDinamicaRtdb[] {
    if (raw == null) return [];
    if (Array.isArray(raw)) {
      return raw.filter((x): x is LandingDinamicaRtdb => x != null && typeof x === 'object');
    }
    if (typeof raw !== 'object') return [];
    return Object.values(raw).filter((v): v is LandingDinamicaRtdb => v != null && typeof v === 'object');
  }

  private filterBySlug(rows: LandingDinamicaRtdb[], slug: string): LandingDinamicaRtdb[] {
    return rows.filter((r) => (r.comercio_slug ?? '') === slug);
  }

  private pickLandingRow(rows: LandingDinamicaRtdb[], programaSlug: string | null | undefined): LandingDinamicaRtdb | undefined {
    const trimmed = programaSlug?.trim();
    return trimmed
      ? rows.find((r) => (r.programa_slug ?? '') === trimmed)
      : rows.find((r) => (r.programa_slug ?? '') === '');
  }

  private fallbackToMock(landing: CommerceLanding | null, slug: string): CommerceLanding | null {
    if (landing != null) return landing;
    return ENVIRONMENT.mockEnabled ? createCommerceLandingMock(slug) : null;
  }
}
